import React, { useState, useRef, useEffect } from 'react';

// Renders a Japanese sentence stem, underlining the target word when provided.
function StemWithUnderline({ stem, underlinedWord, prefix }) {
  const pre = prefix != null ? `${prefix} ` : '';
  if (!stem) return null;
  if (!underlinedWord || !stem.includes(underlinedWord)) {
    return <p className="jeq-stem">{pre}{stem}</p>;
  }
  const parts = stem.split(underlinedWord);
  return (
    <p className="jeq-stem">
      {pre}
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && <u className="jeq-underline">{underlinedWord}</u>}
        </React.Fragment>
      ))}
    </p>
  );
}

// Shared もんだい instruction line (rendered once per page).
function Instruction({ item }) {
  if (!item || (!item.instructionJa && !item.instructionEn)) return null;
  return <p className="jeq-instruction">{item.instructionJa || item.instructionEn}</p>;
}

function QuestionNumber({ pos, total }) {
  if (pos == null || total == null) return null;
  return <p className="jeq-qnum">問題 {pos} / {total}</p>;
}

function ChooseAnswer() {
  return <p className="jeq-choose"><span className="jeq-choose-star">*</span> Choose your answer</p>;
}

// Radio-circle options for one question.
// layout: 'row' renders options side-by-side (used on grouped multi-question pages);
// default is a vertical list.
function RadioOptions({ qid, options, selected, onSelect, variant = 'text', layout = 'column' }) {
  const cls = variant === 'picture'
    ? 'jeq-radio-options jeq-radio-picture'
    : `jeq-radio-options${layout === 'row' ? ' jeq-radio-row' : ''}`;
  return (
    <div className={cls}>
      {options.map((opt) => {
        const isSelected = selected === opt.num;
        return (
          <label key={opt.num} className={`jeq-radio-opt${isSelected ? ' selected' : ''}`}>
            <span className={`jeq-radio-circle${isSelected ? ' checked' : ''}`} />
            <span className="jeq-radio-num">{opt.num}.</span>
            {variant === 'picture' && opt.imageUrl ? (
              <img className="jeq-radio-image" src={opt.imageUrl} alt={opt.visualDesc || `Option ${opt.num}`} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <span className="jeq-radio-text">{opt.text}</span>
            )}
            <input type="radio" className="jeq-radio-input" checked={isSelected} onChange={() => onSelect(opt.num)} />
          </label>
        );
      })}
    </div>
  );
}

// ============================================================================
// Sec A — Vocabulary & Characters (1 question per page)
// ============================================================================
export function VocabQuestion({ page, total, answers, flatPos, onSelect }) {
  const head = page[0];
  return (
    <div className="jeq-q jeq-q-vocab">
      <Instruction item={head} />
      {page.map((q) => {
        const pos = flatPos(q.qid) + 1;
        const isWordUsage = q.type === 'word_usage';
        return (
          <div className="jeq-subq" key={q.qid}>
            <QuestionNumber pos={pos} total={total} />
            {isWordUsage ? (
              <p className="jeq-stem">{pos}. <span className="jeq-target-word">{q.targetWord}</span></p>
            ) : (
              <StemWithUnderline stem={q.stem} underlinedWord={q.underlinedWord} prefix={`${pos}.`} />
            )}
            {q.question && <p className="jeq-question">{q.question}</p>}
            <ChooseAnswer />
            <RadioOptions qid={q.qid} options={q.options} selected={answers[q.qid]} onSelect={(n) => onSelect(q.qid, n)} />
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Sec B — Grammar & Reading
//   - sentence_ordering: stem (with ★/blanks) + options (NO fragments list)
//   - text_cloze / reading: shared passage once, then each sub-question stacked
// ============================================================================
export function GrammarReadingQuestion({ page, total, answers, flatPos, onSelect }) {
  const head = page[0];
  const hasPassage = !!head.passage;
  // On grouped multi-question pages the QUESTIONS sit side-by-side (each a column);
  // the options within a question stay stacked vertically.
  const grouped = page.length > 1;

  const renderSubQ = (q) => {
    const pos = flatPos(q.qid) + 1;
    return (
      <div className={`jeq-subq${grouped ? ' jeq-subq-col' : ''}`} key={q.qid}>
        <QuestionNumber pos={pos} total={total} />
        {q.type === 'sentence_ordering' && q.stem && (
          <p className="jeq-stem jeq-stem-ordering">{pos}. {q.stem}</p>
        )}
        {q.type !== 'sentence_ordering' && !hasPassage && q.stem && (
          <StemWithUnderline stem={q.stem} underlinedWord={q.underlinedWord} prefix={`${pos}.`} />
        )}
        {q.blankNumber != null && (
          <p className="jeq-blank-label">
            {typeof q.blankNumber === 'string' ? q.blankNumber : `（${q.blankNumber}）`}
          </p>
        )}
        {q.question && <p className="jeq-question">{q.question}</p>}
        {!grouped && <ChooseAnswer />}
        <RadioOptions qid={q.qid} options={q.options} selected={answers[q.qid]} onSelect={(n) => onSelect(q.qid, n)} />
      </div>
    );
  };

  // Wrap a block's sub-questions in a row container when grouped (side-by-side).
  const renderSubQs = (qs) => (
    grouped
      ? <div className="jeq-subq-row">{qs.map(renderSubQ)}</div>
      : qs.map(renderSubQ)
  );

  if (hasPassage) {
    // Partition the page into distinct passages (preserving order).
    const blocks = [];
    const idx = {};
    page.forEach((q) => {
      const pid = q.passageId || '_single';
      if (idx[pid] == null) { idx[pid] = blocks.length; blocks.push({ passage: q.passage, questions: [] }); }
      blocks[idx[pid]].questions.push(q);
    });
    return (
      <div className="jeq-q jeq-q-grammar jeq-q-reading">
        <Instruction item={head} />
        {blocks.map((b, bi) => (
          <div className="jeq-passage-block" key={bi}>
            {b.passage && (
              <div className="jeq-passage-box"><p className="jeq-passage">{b.passage}</p></div>
            )}
            {renderSubQs(b.questions)}
          </div>
        ))}
      </div>
    );
  }

  // Non-passage grammar (grammar_form / sentence_ordering / text_cloze).
  return (
    <div className="jeq-q jeq-q-grammar">
      <Instruction item={head} />
      {/* text_cloze shares a single passage shown once at the top. */}
      {head.passage && (
        <div className="jeq-passage-box"><p className="jeq-passage">{head.passage}</p></div>
      )}
      {renderSubQs(page)}
    </div>
  );
}

// ============================================================================
// Sec C — Listening
//   - audio player: single play (play_count), 0:00/0:30, "N play left" chip,
//     waveform bars — styled like the standard exam-prep player.
//   - picture options: SS3 = 4 image tiles labelled 1-4; SS4 = single scene
//     image above 3 radio options.
// ============================================================================
// Audio player — uses the EXACT same markup/classes as the standard Exam Prep
// listening player (ep-audio-block / bw-play-btn / ep-audio-time / ep-audio-track
// / bw-waveform-container / bw-plays-left) so it inherits identical styling:
// square play/pause button, purple-progress waveform, "N / M plays left".
// Play count is owned by the parent (playsUsed/onConsumePlay) so the single-play
// limit PERSISTS across navigation — a question's audio can never be replayed.
function AudioPlayer({ item, playsUsed = 0, onConsumePlay }) {
  const totalPlays = item.playCount || 1;
  const playsLeft = Math.max(0, totalPlays - playsUsed);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    setIsPlaying(false);
    setCur(0); setDur(0);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, [item.qid]);

  const fmt = (s) => {
    const m = Math.floor((s || 0) / 60);
    const sec = Math.floor((s || 0) % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const togglePlay = () => {
    // Pause (does NOT refund the play — it's already been consumed on start).
    if (isPlaying && audioRef.current) { audioRef.current.pause(); setIsPlaying(false); return; }
    if (playsLeft <= 0) return;      // no plays left — permanently locked
    if (!item.audioUrl) return;
    const a = new Audio(item.audioUrl);
    audioRef.current = a;
    a.onloadedmetadata = () => { if (isFinite(a.duration)) setDur(a.duration); };
    a.ontimeupdate = () => setCur(a.currentTime);
    a.onended = () => { setIsPlaying(false); audioRef.current = null; };
    a.onerror = () => setIsPlaying(false);
    a.play().then(() => {
      // Consume one play immediately at start so navigating away mid-play still
      // counts — the audio can be played only once per question.
      if (onConsumePlay) onConsumePlay(item.qid);
      setIsPlaying(true);
    }).catch(() => setIsPlaying(false));
  };

  const progress = dur ? Math.min(100, (cur / dur) * 100) : 0;
  const durShown = dur || Number(item.audioDuration) || 30;
  const locked = playsLeft <= 0 && !isPlaying;
  return (
    <div className="ep-audio-block jeq-audio-block">
      <span className="jeq-audio-label">Audio 1:</span>
      <button type="button" className="bw-play-btn" onClick={togglePlay} disabled={locked} aria-label={isPlaying ? 'Pause' : 'Play'}>
        {isPlaying ? <span className="pause-icon"></span> : <span className="play-icon"></span>}
      </button>
      <span className="ep-audio-time">{fmt(cur)} / {fmt(durShown)}</span>
      <div className="ep-audio-track">
        <div className="bw-waveform-container">
          <svg width="100%" height="24" viewBox="0 0 350 24" preserveAspectRatio="none">
            {Array.from({ length: 80 }).map((_, i) => {
              const heights = [4, 8, 12, 16, 20, 24, 20, 16, 12, 8, 4, 8, 12, 16, 20, 16, 12, 8, 4, 8];
              const h = heights[i % heights.length];
              const played = i < (progress / 100) * 80;
              return <rect key={i} x={i * 4.375} y={(24 - h) / 2} width="2" height={h} fill={played ? '#7500C0' : '#707070'} />;
            })}
          </svg>
        </div>
      </div>
      <div className="bw-plays-left"><strong>{playsLeft}</strong> / {totalPlays} play{totalPlays > 1 ? 's' : ''} left</div>
    </div>
  );
}

export function ListeningQuestion({ page, total, answers, flatPos, onSelect, playsUsed = {}, onConsumePlay }) {
  const item = page[0];
  const pos = flatPos(item.qid) + 1;
  const isPicture = item.optionFormat === 'picture';
  const selected = answers[item.qid];

  return (
    <div className="jeq-q jeq-q-listening">
      <Instruction item={item} />
      <QuestionNumber pos={pos} total={total} />

      <AudioPlayer item={item} playsUsed={playsUsed[item.qid] || 0} onConsumePlay={onConsumePlay} />

      {item.sceneImageUrl && (
        <img className="jeq-scene-image" src={item.sceneImageUrl} alt="Scene" onError={(e) => { e.target.style.display = 'none'; }} />
      )}

      <ChooseAnswer />

      {isPicture ? (
        // Per SS2: the radio controls ("Option 1" … "Option 4") sit OUTSIDE/above
        // in a horizontal row; the small picture tiles (numbered boxes) sit below.
        <div className="jeq-pic-block">
          <div className="jeq-pic-opts">
            {item.options.map((opt) => {
              const isSelected = selected === opt.num;
              return (
                <label key={opt.num} className={`jeq-pic-opt${isSelected ? ' selected' : ''}`}>
                  <span className={`jeq-radio-circle${isSelected ? ' checked' : ''}`} />
                  <span className="jeq-pic-opt-label">Option {opt.num}</span>
                  <input type="radio" className="jeq-radio-input" checked={isSelected} onChange={() => onSelect(item.qid, opt.num)} />
                </label>
              );
            })}
          </div>
          <div className="jeq-pic-grid">
            {item.options.map((opt) => {
              const isSelected = selected === opt.num;
              return (
                <div
                  key={opt.num}
                  className={`jeq-pic-tile${isSelected ? ' selected' : ''}`}
                  onClick={() => onSelect(item.qid, opt.num)}
                  role="button"
                >
                  <span className="jeq-pic-tile-num">{opt.num}</span>
                  {opt.imageUrl
                    ? <img className="jeq-pic-img" src={opt.imageUrl} alt={opt.visualDesc || `Option ${opt.num}`} onError={(e) => { e.target.style.display = 'none'; }} />
                    : <span className="jeq-pic-placeholder">{opt.visualDesc || ''}</span>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // text options: plain radio list (1. 2. 3.).
        <RadioOptions qid={item.qid} options={item.options} selected={selected} onSelect={(n) => onSelect(item.qid, n)} />
      )}
    </div>
  );
}
